import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useNotifications } from '../../hooks/useNotifications';
import * as categoryService from '../../services/categories';
import * as folderService from '../../services/folders';
import { toast } from 'react-hot-toast';
import { FiMenu, FiX, FiBell, FiSearch, FiUser, FiLogOut, FiHome, FiFileText, FiFolder, FiGrid, FiUsers, FiSettings, FiBarChart2, FiHelpCircle, FiChevronDown } from 'react-icons/fi';
import { MdDashboard, MdArchive, MdNotificationsActive } from 'react-icons/md';
import { HiDocumentSearch } from 'react-icons/hi';
import { BsFillCalendarEventFill } from 'react-icons/bs';
import './Layout.scss';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [folderForm, setFolderForm] = useState({
    name: '',
    categoryId: '',
    parentFolderId: '',
  });

  const { user, logout } = useAuth();
  const { socket } = useSocket();
  const { notifications, markAsRead, getUnreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation items
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <MdDashboard />, exact: true },
    { path: '/documents', label: 'Documents', icon: <FiFileText /> },
    { path: '/categories', label: 'Categories', icon: <FiGrid /> },
    { path: '/folders', label: 'Folders', icon: <FiFolder /> },
    { path: '/search', label: 'Advanced Search', icon: <HiDocumentSearch /> },
    { path: '/admin/users', label: 'Users', icon: <FiUsers />, adminOnly: true },
    { path: '/admin/audit-logs', label: 'Audit Logs', icon: <MdArchive />, adminOnly: true },
    { path: '/admin/reports', label: 'Reports', icon: <FiBarChart2 />, adminOnly: true },
    { path: '/settings', label: 'Settings', icon: <FiSettings /> },
  ];

  // Check if user has admin access
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  const { data: createFolderCategories } = useQuery(
    ['create-folder-categories'],
    () => categoryService.getCategories({ page: 1, limit: 100 }),
    {
      enabled: createFolderOpen,
    }
  );

  const { data: createFolderFolders } = useQuery(
    ['create-folder-folders', folderForm.categoryId],
    () =>
      folderForm.categoryId
        ? folderService.getFolders({ categoryId: folderForm.categoryId })
        : Promise.resolve({ folders: [] }),
    {
      enabled: createFolderOpen && !!folderForm.categoryId,
    }
  );

  const createFolderMutation = useMutation(
    (payload) => folderService.createFolder(payload),
    {
      onSuccess: (response) => {
        const folder =
          response?.data?.data ||
          response?.data ||
          response;
        setCreateFolderOpen(false);
        setFolderForm({
          name: '',
          categoryId: '',
          parentFolderId: '',
        });
        toast.success('Folder created successfully');
        if (folder && folder._id) {
          navigate(`/folders/${folder._id}`);
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || error.message || 'Failed to create folder');
      },
    }
  );

  const handleCreateFolderSubmit = (event) => {
    event.preventDefault();
    if (!folderForm.name || !folderForm.categoryId) {
      toast.error('Name and category are required');
      return;
    }
    createFolderMutation.mutate({
      name: folderForm.name,
      description: '',
      categoryId: folderForm.categoryId,
      parentFolderId: folderForm.parentFolderId || null,
    });
  };

  // Update unread count
  useEffect(() => {
    const updateUnreadCount = async () => {
      const count = await getUnreadCount();
      setUnreadCount(count);
    };
    updateUnreadCount();
  }, [notifications, getUnreadCount]);

  // Handle real-time notifications
  useEffect(() => {
    if (socket) {
      socket.on('notification', (notification) => {
        // Show toast notification
        console.log('New notification:', notification);
        // Update unread count
        setUnreadCount(prev => prev + 1);
      });

      return () => {
        socket.off('notification');
      };
    }
  }, [socket]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    await markAsRead(notification._id);
    if (notification.documentId) {
      navigate(`/documents/${notification.documentId}`);
    }
    setNotificationsOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownOpen && !event.target.closest('.user-dropdown')) {
        setUserDropdownOpen(false);
      }
      if (notificationsOpen && !event.target.closest('.notifications-dropdown')) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen, notificationsOpen]);

  // Responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <div className="layout">
      {/* Top Navigation Bar */}
      <header className="header">
        <div className="header-left">
          <button
            className="menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
          
          <div className="logo">
            <div className="logo-circle">
              {/* This is where your circle style logo goes */}
              <span>OMJ</span>
            </div>
            <div className="logo-text">
              <h1>OMJ DMS</h1>
              <span className="logo-subtitle">Document Management System</span>
            </div>
          </div>
        </div>

        <div className="header-center">
          <form onSubmit={handleSearch} className={`search-form ${searchOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="search-toggle"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Toggle search"
            >
              <FiSearch size={20} />
            </button>
            <input
              type="text"
              className="search-input"
              placeholder="Search documents, categories, folders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
            />
            <button type="submit" className="search-submit" aria-label="Search">
              <FiSearch size={20} />
            </button>
          </form>
        </div>

        <div className="header-right">
          {/* Notifications */}
          <div className="notifications-dropdown">
            <button
              className="notification-btn"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              aria-label="Notifications"
            >
              <FiBell size={20} />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>
            
            <AnimatePresence>
              {notificationsOpen && (
                <motion.div
                  className="notifications-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className="notifications-header">
                    <h3>Notifications</h3>
                    <button
                      className="btn-mark-all-read"
                      onClick={() => {/* Implement mark all as read */}}
                    >
                      Mark all as read
                    </button>
                  </div>
                  <div className="notifications-list">
                    {notifications.slice(0, 5).map(notification => (
                      <div
                        key={notification._id}
                        className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="notification-icon">
                          <MdNotificationsActive />
                        </div>
                        <div className="notification-content">
                          <p className="notification-message">{notification.message}</p>
                          <span className="notification-time">
                            {new Date(notification.createdAt).toLocaleTimeString([], { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                    {notifications.length === 0 && (
                      <div className="no-notifications">
                        <p>No notifications</p>
                      </div>
                    )}
                  </div>
                  <div className="notifications-footer">
                    <button
                      className="btn-view-all"
                      onClick={() => {
                        navigate('/notifications');
                        setNotificationsOpen(false);
                      }}
                    >
                      View all notifications
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Dropdown */}
          <div className="user-dropdown">
            <button
              className="user-btn"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              aria-label="User menu"
            >
              <div className="user-avatar">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} />
                ) : (
                  <span>{user?.name?.charAt(0)}</span>
                )}
              </div>
              <span className="user-name">{user?.name}</span>
              <FiChevronDown className={`dropdown-arrow ${userDropdownOpen ? 'rotate' : ''}`} />
            </button>
            
            <AnimatePresence>
              {userDropdownOpen && (
                <motion.div
                  className="user-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <div className="user-info">
                    <div className="user-avatar-large">
                      {user?.avatar ? (
                        <img src={user.avatar} alt={user.name} />
                      ) : (
                        <span>{user?.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="user-details">
                      <h4>{user?.name}</h4>
                      <p>{user?.email}</p>
                      <span className="user-role">{user?.role}</span>
                    </div>
                  </div>
                  <div className="user-menu-items">
                    <button
                      className="menu-item"
                      onClick={() => {
                        navigate('/profile');
                        setUserDropdownOpen(false);
                      }}
                    >
                      <FiUser />
                      <span>Profile</span>
                    </button>
                    <button
                      className="menu-item"
                      onClick={() => {
                        navigate('/settings');
                        setUserDropdownOpen(false);
                      }}
                    >
                      <FiSettings />
                      <span>Settings</span>
                    </button>
                    <div className="menu-divider" />
                    <button className="menu-item logout" onClick={handleLogout}>
                      <FiLogOut />
                      <span>Logout</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-container">
        {createFolderOpen && (
          <div className="modal-overlay" onClick={() => setCreateFolderOpen(false)}>
            <div className="modal" onClick={(event) => event.stopPropagation()}>
              <h2>Create Folder</h2>
              <form onSubmit={handleCreateFolderSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={folderForm.name}
                    onChange={(event) =>
                      setFolderForm((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    className="form-control"
                    value={folderForm.categoryId}
                    onChange={(event) =>
                      setFolderForm((prev) => ({
                        ...prev,
                        categoryId: event.target.value,
                        parentFolderId: '',
                      }))
                    }
                  >
                    <option value="">Select category</option>
                    {(createFolderCategories?.data?.data ||
                      createFolderCategories?.data ||
                      createFolderCategories?.categories ||
                      []).map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Parent folder (optional)</label>
                  <select
                    className="form-control"
                    value={folderForm.parentFolderId}
                    onChange={(event) =>
                      setFolderForm((prev) => ({
                        ...prev,
                        parentFolderId: event.target.value,
                      }))
                    }
                    disabled={!folderForm.categoryId}
                  >
                    <option value="">No parent (top level)</option>
                    {(createFolderFolders?.data?.data ||
                      createFolderFolders?.data ||
                      createFolderFolders?.folders ||
                      []).map((folder) => (
                      <option key={folder._id} value={folder._id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setCreateFolderOpen(false)}
                    disabled={createFolderMutation.isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createFolderMutation.isLoading}
                  >
                    {createFolderMutation.isLoading ? 'Creating...' : 'Create Folder'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Mobile Sidebar */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="mobile-sidebar-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>
        
        <motion.aside
          className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}
          animate={{
            width: sidebarOpen ? 280 : 80,
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {/* Desktop Sidebar */}
          <nav className="sidebar-nav">
            <ul className="nav-items">
              {filteredNavItems.map((item) => {
                const isActive = item.exact
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                
                return (
                  <li key={item.path} className="nav-item">
                    <button
                      className={`nav-link ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        navigate(item.path);
                        if (window.innerWidth < 1024) {
                          setMobileMenuOpen(false);
                        }
                      }}
                      title={item.label}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <motion.span
                        className="nav-label"
                        initial={{ opacity: sidebarOpen ? 1 : 0 }}
                        animate={{ opacity: sidebarOpen ? 1 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.label}
                      </motion.span>
                      {isActive && (
                        <motion.div
                          className="nav-active-indicator"
                          layoutId="activeIndicator"
                          transition={{ duration: 0.2 }}
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Quick Actions */}
            <div className="quick-actions">
              <h3 className="quick-actions-title">Quick Actions</h3>
              <div className="quick-action-buttons">
                <button
                  className="quick-action-btn"
                  onClick={() => navigate('/documents/create')}
                >
                  <FiFileText />
                  <span>New Document</span>
                </button>
                <button
                  className="quick-action-btn"
                  onClick={() => setCreateFolderOpen(true)}
                >
                  <FiFolder />
                  <span>New Folder</span>
                </button>
                <button
                  className="quick-action-btn"
                  onClick={() => {/* Implement scan document */}}
                >
                  <HiDocumentSearch />
                  <span>Scan Document</span>
                </button>
              </div>
            </div>

            {/* System Status */}
            <div className="system-status">
              <div className="status-item">
                <div className="status-dot online" />
                <span className="status-text">System Online</span>
              </div>
              <div className="status-item">
                <BsFillCalendarEventFill className="status-icon" />
                <span className="status-text">
                  {new Date().toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </div>

            {/* Help & Support */}
            <div className="sidebar-help">
              <button
                className="help-btn"
                onClick={() => {/* Implement help modal */}}
              >
                <FiHelpCircle />
                <span>Help & Support</span>
              </button>
            </div>
          </nav>

          {/* Sidebar Toggle */}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <FiChevronDown className={`toggle-arrow ${sidebarOpen ? '' : 'rotate'}`} />
          </button>
        </motion.aside>

        {/* Main Content Area */}
        <main className="main-content">
          <div className="content-wrapper">
            {/* Breadcrumb */}
            <nav className="breadcrumb">
              <button className="breadcrumb-home" onClick={() => navigate('/dashboard')}>
                <FiHome />
              </button>
              {location.pathname.split('/').filter(Boolean).map((segment, index, array) => (
                <React.Fragment key={segment}>
                  <span className="breadcrumb-separator">/</span>
                  <button
                    className={`breadcrumb-item ${index === array.length - 1 ? 'active' : ''}`}
                    onClick={() => {
                      const path = '/' + array.slice(0, index + 1).join('/');
                      navigate(path);
                    }}
                  >
                    {segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ')}
                  </button>
                </React.Fragment>
              ))}
            </nav>

            {/* Page Content */}
            <div className="page-content">
              <AnimatePresence mode="wait">
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Outlet />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <p>&copy; {new Date().getFullYear()} Document Management System. All rights reserved.</p>
          </div>
          <div className="footer-right">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
